import { useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';

/**
 * Hook for client access control.
 * Provides utilities to check user's client access based on Keycloak groups.
 */
export const useClientAccess = () => {
  const { user } = useAuth();

  return useMemo(() => {
    // Check if user is SuperAdmin (has access to all clients)
    const isSuperAdmin = user?.roles.includes('SuperAdmin') ?? false;
    const isAdmin = user?.roles.includes('Admin') ?? false;
    const isClientAdmin = user?.roles.includes('ClientAdmin') ?? false;
    const isOperator = user?.roles.includes('Operator') ?? false;
    const isViewer = user?.roles.includes('Viewer') ?? false;

    // Backward compat: Operator treated as ClientAdmin equivalent
    const isClientAdminOrOperator = isClientAdmin || isOperator;

    // Get the list of client IDs the user has access to
    const allowedClientIds = user?.clientIds ?? [];

    // Check if user has only Viewer role (no Admin, SuperAdmin, ClientAdmin, or Operator)
    const hasOnlyViewerRole = isViewer && !isSuperAdmin && !isAdmin && !isClientAdminOrOperator;

    // Users section: visible to ClientAdmin, Admin, SuperAdmin
    const canManageUsers = isSuperAdmin || isAdmin || isClientAdminOrOperator;

    // Device types: ClientAdmin+ can edit settings, hardware types, model refs
    const canEditDeviceTypes = isSuperAdmin || isAdmin || isClientAdminOrOperator;

    // Check if user has only one client assigned
    const hasSingleClient = allowedClientIds.length === 1;

    return {
      /**
       * Whether the user is a SuperAdmin with unrestricted access
       */
      isSuperAdmin,

      /**
       * Whether the user is an Admin
       */
      isAdmin,

      /**
       * Whether the user is a ClientAdmin
       */
      isClientAdmin,

      /**
       * Whether the user is an Operator
       */
      isOperator,

      /**
       * Whether the user is a Viewer only
       */
      isViewerOnly: hasOnlyViewerRole,

      /**
       * Whether the user can access the Users section
       */
      canManageUsers,

      /**
       * Whether the user can edit device types, hardware types, and model references
       */
      canEditDeviceTypes,

      /**
       * List of client IDs the user has access to
       * Empty for SuperAdmin (they have access to all)
       */
      allowedClientIds,

      /**
       * Whether the user has full (unrestricted) access to all data
       */
      hasFullAccess: isSuperAdmin,

      /**
       * Whether user has only one client assigned
       */
      hasSingleClient,

      /**
       * Whether to show client-related UI (Clients menu, client stats).
       * Hidden for Viewer-only users.
       *
       * SuperAdmin / Admin always see the Clients item — they need it
       * to onboard additional tenants. The previous `!hasSingleClient`
       * gate locked an Admin out of the tenant-creation flow once they
       * were assigned to a single client (the typical Admin setup
       * coming out of Phase 0 onboarding), which is a UX dead-end.
       * ClientAdmin / Operator scoped to a single client still see
       * it because POST /v2/client is gated by require_admin on the
       * backend (they'll get a 403 if they try) — surfacing the menu
       * doesn't grant new powers, just removes a confusing hide.
       */
      shouldShowClientUI: !hasOnlyViewerRole,

      /**
       * Check if user can access a specific client
       * @param clientId - The client ID to check
       * @returns true if user can access the client
       */
      canAccessClient: (clientId: string): boolean => {
        if (isSuperAdmin) return true;
        return allowedClientIds.includes(clientId);
      },

      /**
       * Filter an array to only include items the user can access
       * @param items - Array of items with client_id property
       * @returns Filtered array
       */
      filterByClientAccess: <T extends { client_id?: string | null }>(items: T[]): T[] => {
        if (isSuperAdmin) return items;
        return items.filter(item => {
          // Items without client_id (global) are accessible to everyone
          if (!item.client_id) return true;
          return allowedClientIds.includes(item.client_id);
        });
      },

      /**
       * Check if user has any client assignments
       * SuperAdmin always returns true even without explicit assignments
       */
      hasAnyClientAccess: isSuperAdmin || allowedClientIds.length > 0,

      /**
       * Whether the user can view the audit log page.
       * Restricted to Admin and SuperAdmin (matches backend require_admin gate).
       */
      canViewAuditLog: isSuperAdmin || isAdmin,

      /**
       * Whether the user can create / edit devices. SuperAdmin always
       * allowed; Admin / ClientAdmin / Operator must additionally have
       * at least one client assignment, otherwise the backend's
       * allowed_clients filter would refuse the POST anyway (and the
       * UI affordance would mislead). Viewer-only never qualifies.
       */
      canManageDevices: isSuperAdmin || ((isAdmin || isClientAdminOrOperator) && allowedClientIds.length > 0)
    };
  }, [user?.roles, user?.clientIds]);
};

export default useClientAccess;
